/**
 * GAME 2 — MIROIRS (laser). Rayon qui rebondit sur des miroirs \ ou /.
 * Refonte difficulté (branche difficulte-score) — n'est PLUS la migration à
 * l'identique du prototype : cases vides (le rayon file droit), miroirs soudés
 * (fixes, non basculables), murs absorbants, générateur contraint (distance
 * périmétrique entrée→cible, longueur de chemin, rebonds, anti-loterie) et
 * brouillage ciblé des miroirs du chemin solution.
 *
 * Encodage cellule (sol et cur, même grille d'entiers, JSON-sérialisable) :
 *   0/1 = miroir BASCULABLE, 2/3 = miroir FIXE (orientation = v&1),
 *   4 = VIDE (le rayon traverse), 5 = MUR (le rayon est absorbé).
 * Seuls 0/1 réagissent au tap. `miTrace` et `periDist` sont exportés pour le
 * guard test (guard-miroirs.test.ts).
 */
import type { GameEngine, PuzzleBase } from '@ordre-cache/shared';
import { ri, shuffle } from '@ordre-cache/shared';
import { D4, deep, mk } from './util';

interface Side {
  side: string;
  idx: number;
}

export interface MiroirsPuzzle extends PuzzleBase {
  g: 'miroirs';
  ents: Side[];
  targets: Side[];
  gems: [number, number][];
  bombs: [number, number][];
  K: number | null;
  sol: number[][];
  cur: number[][];
}

interface MiTrace {
  exit: Side | null;
  /** true si le rayon est mort dans un mur (valeur 5). */
  abs: boolean;
  pts: [number, number][];
  cells: number[];
  segs: number;
}

interface MiInput {
  N: number;
  ents: Side[];
  targets: Side[];
  gems: [number, number][];
  bombs: [number, number][];
  K: number | null;
  cur: number[][];
}

type MiResult =
  { ok: true } | { ok: false; hit?: number; gem?: boolean; bomb?: boolean; K?: number };

const MREF: Record<number, number>[] = [
  { 0: 1, 1: 0, 2: 3, 3: 2 },
  { 0: 3, 3: 0, 1: 2, 2: 1 },
];

/**
 * Trace un rayon. `pts` ne contient que entrée + rebonds + sortie : `segs`
 * compte donc les vrais segments (rebonds + 1), base du défi « Rebonds comptés ».
 */
export function miTrace(cur: number[][], N: number, ent: Side): MiTrace {
  let r: number, c: number, d: number;
  if (ent.side === 'W') {
    r = ent.idx;
    c = 0;
    d = 1;
  } else {
    r = 0;
    c = ent.idx;
    d = 2;
  }
  const pts: [number, number][] = [ent.side === 'W' ? [0, ent.idx + 0.5] : [ent.idx + 0.5, 0]];
  const cells: number[] = [];
  let steps = 0;
  while (r >= 0 && r < N && c >= 0 && c < N) {
    cells.push(r * N + c);
    const v = cur[r][c];
    if (v === 5) {
      pts.push([c + 0.5, r + 0.5]);
      return { exit: null, abs: true, pts, cells, segs: pts.length - 1 };
    }
    if (v !== 4) {
      const nd = MREF[v & 1][d];
      if (nd !== d) pts.push([c + 0.5, r + 0.5]);
      d = nd;
    }
    if (++steps > 4 * N * N) return { exit: null, abs: false, pts, cells, segs: pts.length - 1 };
    const pr = r,
      pc = c;
    if (d === 0) r--;
    else if (d === 1) c++;
    else if (d === 2) r++;
    else c--;
    if (r < 0 || r >= N || c < 0 || c >= N) {
      let side: string, idx: number, ex: number, ey: number;
      if (d === 1) {
        side = 'E';
        idx = pr;
        ex = N;
        ey = pr + 0.5;
      } else if (d === 3) {
        side = 'W';
        idx = pr;
        ex = 0;
        ey = pr + 0.5;
      } else if (d === 2) {
        side = 'S';
        idx = pc;
        ex = pc + 0.5;
        ey = N;
      } else {
        side = 'N';
        idx = pc;
        ex = pc + 0.5;
        ey = 0;
      }
      pts.push([ex, ey]);
      return { exit: { side, idx }, abs: false, pts, cells, segs: pts.length - 1 };
    }
  }
  return { exit: null, abs: false, pts, cells, segs: pts.length - 1 };
}

/** Position linéarisée sur le périmètre (longueur 4N, sens horaire depuis NW). */
function periPos(N: number, s: Side): number {
  if (s.side === 'N') return s.idx;
  if (s.side === 'E') return N + s.idx;
  if (s.side === 'S') return 2 * N + (N - 1 - s.idx);
  return 3 * N + (N - 1 - s.idx);
}
/** Distance périmétrique min entre deux points du bord — exporté pour les tests. */
export function periDist(N: number, a: Side, b: Side): number {
  const d = Math.abs(periPos(N, a) - periPos(N, b));
  return Math.min(d, 4 * N - d);
}

function miOkNow(p: MiInput): MiResult {
  const N = p.N;
  const allCells = new Set<number>();
  let segsTot = 0;
  for (let i = 0; i < p.ents.length; i++) {
    const t = miTrace(p.cur, N, p.ents[i]);
    if (!t.exit || t.exit.side !== p.targets[i].side || t.exit.idx !== p.targets[i].idx)
      return { ok: false, hit: i };
    t.cells.forEach((c) => allCells.add(c));
    segsTot += t.segs;
  }
  for (const g of p.gems) if (!allCells.has(g[0] * N + g[1])) return { ok: false, gem: true };
  for (const b of p.bombs) if (allCells.has(b[0] * N + b[1])) return { ok: false, bomb: true };
  if (p.K && segsTot !== p.K) return { ok: false, K: segsTot };
  return { ok: true };
}

export const MIROIRS: GameEngine<MiroirsPuzzle> = {
  id: 'miroirs',
  name: 'Miroirs',
  icon: '🪞',
  accent: '#4cc9f0',
  tag: 'Bascule les miroirs, faufile le rayon entre les murs.',
  how: "<b>Touche un miroir clair</b> pour le basculer (\\ ou /). Le rayon file tout droit sur les cases vides, rebondit sur les miroirs — mais les miroirs gris sont <b>soudés</b> (impossibles à basculer) et les murs sombres <b>absorbent</b> le rayon. But : qu'il sorte <b>pile sur la cible 💎</b> — en respectant les contraintes du défi.",
  sizes: [5, 10, 20, 26],
  defis: [
    { n: 'La cible', d: "Guide le rayon de miroir en miroir jusqu'à la gemme.", ic: '🎯' },
    { n: 'Collier de gemmes', d: 'Le rayon doit enfiler chaque gemme posée sur son trajet.', ic: '📿' },
    { n: 'Champ de mines', d: 'Des mines collées au trajet : un frôlement et boum.', ic: '💣' },
    { n: 'Double rayon', d: 'Deux lasers, deux cibles, et des miroirs à partager.', ic: '⚡' },
    { n: 'Rebonds comptés', d: 'Touche la cible avec le bon nombre de rebonds, pile-poil.', ic: '↩️' },
    { n: 'La totale', d: 'Gemmes, mines, double rayon : tout à la fois.', ic: '👑' },
  ],
  gen(di, N, diff, rng) {
    const twoRays = di === 3 || di === 5;
    const emptyFrac = [0.4, 0.32, 0.25][diff];
    const distTh = N * [1.0, 1.5, 2.0][diff];
    const lenTh = N * [1.6, 2.2, 3.0][diff] * (twoRays ? 0.7 : 1);
    const segTh = [4, 6, 9][diff] * (twoRays ? 0.7 : 1);
    const kSegTh = N * [1.2, 2.0, 2.8][diff];
    const lotCap = [0.1, 0.05, 0.025][diff];
    const nWalls = [2, 3, 5][diff];
    const lockFrac = [0.1, 0.2, 0.3][diff];
    const kFrac = [0.35, 0.5, 0.65][diff];
    const kMin = [2, 4, 6][diff];
    const ng = di === 1 || di === 5 ? [1, 2, 3][diff] : 0;

    let best: MiroirsPuzzle | null = null;
    let bestScore = -1;

    // Rayon unique : entrée W ou N (2N candidats) ; double : W puis N.
    const raySides: string[][] = twoRays ? [['W'], ['N']] : [['W', 'N']];
    // 500 tentatives (relâchement à 380 puis 450) : nécessaire pour tenir le
    // seuil plein niveau 2 en 5×5 (cible antipodale + chemin ≥ 3N) sans repli.
    for (let attempt = 0; attempt < 500; attempt++) {
      const relax = attempt >= 450 ? 0.5 : attempt >= 380 ? 0.75 : 1;
      // 1. composition : cases vides + miroirs orientés au hasard
      const sol = mk(N, 0).map((row) => row.map(() => (rng() < emptyFrac ? 4 : ri(rng, 2))));
      // 2. entrées : pour chaque rayon, essayer les candidats dans un ordre mélangé
      const ents: Side[] = [];
      const traces: MiTrace[] = [];
      let strict = true;
      let dead = false;
      for (const sides of raySides) {
        let found: { e: Side; t: MiTrace } | null = null;
        let fb: { e: Side; t: MiTrace; s: number } | null = null;
        const cands: Side[] = [];
        for (const side of sides)
          for (let idx = 0; idx < N; idx++) cands.push({ side, idx });
        shuffle(rng, cands);
        for (const e of cands) {
          const t = miTrace(sol, N, e);
          if (!t.exit || (t.exit.side === e.side && t.exit.idx === e.idx)) continue;
          const d = periDist(N, e, t.exit);
          const s =
            Math.min(1, d / distTh) +
            Math.min(1, t.cells.length / lenTh) +
            Math.min(1, t.segs / segTh);
          if (!fb || s > fb.s) fb = { e, t, s };
          if (
            d >= distTh * relax &&
            t.cells.length >= lenTh * relax &&
            t.segs >= segTh * relax &&
            (di !== 4 || t.segs >= kSegTh * relax)
          ) {
            found = { e, t };
            break;
          }
        }
        if (!found && !fb) {
          dead = true;
          break;
        }
        if (!found) strict = false;
        const pick = found ?? fb!;
        ents.push(pick.e);
        traces.push(pick.t);
      }
      if (dead) continue;
      // Un repli est déjà en réserve : ne pas construire les tentatives hors seuils.
      if (!strict && best && attempt < 440) continue;
      const onPath = new Set<number>();
      traces.forEach((t) => t.cells.forEach((c) => onPath.add(c)));
      // 3. gemmes : cellules DISTINCTES espacées le long du/des chemin(s)
      const gems: [number, number][] = [];
      if (ng) {
        const used = new Set<number>();
        let fail = false;
        for (let i = 0; i < ng && !fail; i++) {
          const ray = di === 5 ? i % ents.length : 0; // di5 : alternance sur les 2 rayons
          const want = di === 5 ? Math.ceil((ng - ray) / ents.length) : ng;
          const j = di === 5 ? Math.floor(i / ents.length) : i;
          const inter = traces[ray].cells.slice(1, -1);
          if (!inter.length) {
            fail = true;
            break;
          }
          const at = Math.min(
            inter.length - 1,
            Math.max(0, Math.round(((j + 1) * inter.length) / (want + 1))),
          );
          let cell = -1;
          for (let off = 0; off < inter.length && cell < 0; off++) {
            const a = at - off,
              b = at + off;
            if (a >= 0 && !used.has(inter[a])) cell = inter[a];
            else if (b < inter.length && !used.has(inter[b])) cell = inter[b];
          }
          if (cell < 0) fail = true;
          else {
            used.add(cell);
            gems.push([Math.floor(cell / N), cell % N]);
          }
        }
        if (fail || gems.length < ng) continue;
      }
      // 4. mines : majorité 4-adjacentes au chemin, le reste au hasard hors chemin
      const bombs: [number, number][] = [];
      if (di === 2 || di === 5) {
        const nb = [3, 5, 8][diff];
        const adj: number[] = [],
          far: number[] = [];
        for (let i = 0; i < N * N; i++) {
          if (onPath.has(i)) continue;
          const r0 = Math.floor(i / N),
            c0 = i % N;
          const near = D4.some(([dr, dc]) => {
            const rr = r0 + dr,
              cc = c0 + dc;
            return rr >= 0 && rr < N && cc >= 0 && cc < N && onPath.has(rr * N + cc);
          });
          (near ? adj : far).push(i);
        }
        shuffle(rng, adj);
        shuffle(rng, far);
        const nAdj = Math.min(adj.length, Math.round(nb * [0.6, 0.75, 0.9][diff]));
        const chosen = adj.slice(0, nAdj);
        const rest = far.concat(adj.slice(nAdj));
        for (let i = 0; chosen.length < nb && i < rest.length; i++) chosen.push(rest[i]);
        chosen.forEach((i) => bombs.push([Math.floor(i / N), i % N]));
      }
      const K = di === 4 ? traces.reduce((s, t) => s + t.segs, 0) : null;
      const targets = traces.map((t) => t.exit!);
      // 5. murs HORS chemin (le tracé solution ne change pas), jamais sur une mine
      const bombSet = new Set(bombs.map((b) => b[0] * N + b[1]));
      const freeW: number[] = [];
      for (let i = 0; i < N * N; i++) if (!onPath.has(i) && !bombSet.has(i)) freeW.push(i);
      shuffle(rng, freeW);
      for (const i of freeW.slice(0, nWalls)) sol[Math.floor(i / N)][i % N] = 5;
      // 6. verrous : +2 sur une fraction des miroirs — niveau 2 : 2 waypoints du chemin
      const pathM: number[] = [],
        offM: number[] = [];
      for (let i = 0; i < N * N; i++) {
        const v = sol[Math.floor(i / N)][i % N];
        if (v === 0 || v === 1) (onPath.has(i) ? pathM : offM).push(i);
      }
      shuffle(rng, pathM);
      shuffle(rng, offM);
      const nLock = Math.round(lockFrac * (pathM.length + offM.length));
      const locks: number[] = [];
      if (diff === 2)
        locks.push(...pathM.slice(0, Math.min(2, nLock, Math.max(0, pathM.length - 3))));
      for (let i = 0; locks.length < nLock && i < offM.length; i++) locks.push(offM[i]);
      for (const i of locks) sol[Math.floor(i / N)][i % N] += 2;
      const lockSet = new Set(locks);
      const togglePath = pathM.filter((i) => !lockSet.has(i));
      const toggleOff = offM.filter((i) => !lockSet.has(i));
      if (togglePath.length < 2) continue; // impossible de brouiller le chemin
      // 7. anti-loterie : 40 réassignations aléatoires des seuls basculables
      const allT = togglePath.concat(toggleOff);
      const g = sol.map((row) => row.slice());
      let wins = 0;
      for (let s = 0; s < 40; s++) {
        for (const i of allT) g[Math.floor(i / N)][i % N] = ri(rng, 2);
        if (miOkNow({ N, ents, targets, gems, bombs, K, cur: g }).ok) wins++;
      }
      const rate = wins / 40;
      if (rate > lotCap / relax) strict = false;
      // 8. brouillage ciblé : k bascules SUR le chemin + ~30 % hors chemin
      const k = Math.min(togglePath.length, Math.max(kMin, Math.round(togglePath.length * kFrac)));
      let cur: number[][] | null = null;
      for (let s = 0; s < 60 && !cur; s++) {
        const cand = sol.map((row) => row.slice());
        shuffle(rng, togglePath);
        for (let j = 0; j < k; j++) cand[Math.floor(togglePath[j] / N)][togglePath[j] % N] ^= 1;
        for (const i of toggleOff) if (rng() < 0.3) cand[Math.floor(i / N)][i % N] ^= 1;
        // valider : CHAQUE rayon rate individuellement SA cible (⇒ status non-ok)
        let miss = true;
        for (let i = 0; i < ents.length && miss; i++) {
          const t = miTrace(cand, N, ents[i]);
          if (t.exit && t.exit.side === targets[i].side && t.exit.idx === targets[i].idx)
            miss = false;
        }
        if (miss) cur = cand;
      }
      if (!cur) continue;
      let par = 0;
      for (let i = 0; i < N * N; i++)
        if (cur[Math.floor(i / N)][i % N] !== sol[Math.floor(i / N)][i % N]) par++;
      const puz: MiroirsPuzzle = {
        g: 'miroirs',
        pid: ri(rng, 1e9),
        N,
        di,
        par,
        ents,
        targets,
        gems,
        bombs,
        K,
        sol,
        cur,
      };
      if (strict) return puz;
      // repli : garder le meilleur candidat rencontré (score de contraintes)
      let sc = 0;
      for (let i = 0; i < ents.length; i++)
        sc +=
          Math.min(1, periDist(N, ents[i], targets[i]) / distTh) +
          Math.min(1, traces[i].cells.length / lenTh) +
          Math.min(1, traces[i].segs / segTh);
      if (di === 4) sc += Math.min(1, (K ?? 0) / kSegTh);
      sc += Math.min(1, lotCap / Math.max(rate, 1e-3));
      if (sc > bestScore) {
        bestScore = sc;
        best = puz;
      }
    }
    if (best) return best;
    // repli ultime (jamais atteint en pratique) : grille uniforme entièrement brouillée
    const solF = mk(N, 0);
    const entF: Side = { side: 'W', idx: 0 };
    return {
      g: 'miroirs',
      pid: ri(rng, 1e9),
      N,
      di,
      par: N * N,
      ents: [entF],
      targets: [miTrace(solF, N, entF).exit!],
      gems: [],
      bombs: [],
      K: null,
      sol: solF,
      cur: solF.map((r) => r.map((v) => v ^ 1)),
    };
  },
  tap(p, r, c) {
    if (p.cur[r][c] >= 2) return false; // miroir soudé, case vide ou mur : rien ne bouge
    p.cur[r][c] ^= 1;
    return true;
  },
  status(p) {
    const res = miOkNow(p);
    if (res.ok) return { ok: true, txt: '🎉 <b>Le rayon atteint sa cible !</b>' };
    const traces = p.ents.map((e) => miTrace(p.cur, p.N, e));
    const hits = traces.filter(
      (t, i) => t.exit && t.exit.side === p.targets[i].side && t.exit.idx === p.targets[i].idx,
    ).length;
    const allCells = new Set<number>();
    traces.forEach((t) => t.cells.forEach((c) => allCells.add(c)));
    const bits = [`🎯 cibles <b>${hits}/${p.ents.length}</b>`];
    if (traces.some((t) => t.abs)) bits.push('💥 rayon absorbé par un mur');
    if (p.gems.length)
      bits.push(
        `💎 ${p.gems.filter((g) => allCells.has(g[0] * p.N + g[1])).length}/${p.gems.length}`,
      );
    if (p.bombs.length) {
      const boom = p.bombs.some((b) => allCells.has(b[0] * p.N + b[1]));
      bits.push(boom ? '💥 mine touchée !' : '💣 ok');
    }
    if (p.K) bits.push(`↩ rebonds ${traces.reduce((s, t) => s + t.segs, 0)}/${p.K}`);
    return { ok: false, txt: bits.join(' · ') };
  },
  draw(p, ctx, layout) {
    const S = layout.size,
      N = p.N,
      cs = S / N;
    ctx.fillStyle = '#0b1020';
    ctx.fillRect(0, 0, S, S);
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    for (let i = 1; i < N; i++) {
      ctx.beginPath();
      ctx.moveTo(i * cs, 0);
      ctx.lineTo(i * cs, S);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * cs);
      ctx.lineTo(S, i * cs);
      ctx.stroke();
    }
    ctx.lineCap = 'round';
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        const v = p.cur[r][c];
        const x0 = c * cs,
          y0 = r * cs;
        if (v === 4) {
          // case vide : fond nu, un point discret
          ctx.fillStyle = 'rgba(174,191,224,0.18)';
          ctx.beginPath();
          ctx.arc(x0 + cs / 2, y0 + cs / 2, Math.max(1, cs * 0.045), 0, Math.PI * 2);
          ctx.fill();
        } else if (v === 5) {
          // mur : bloc arrondi sombre, bord rouge discret
          const m = cs * 0.12,
            rad = cs * 0.12,
            x = x0 + m,
            y = y0 + m,
            w = cs - 2 * m;
          ctx.fillStyle = '#3a2333';
          ctx.strokeStyle = 'rgba(255,84,112,0.4)';
          ctx.lineWidth = Math.max(1, cs * 0.04);
          ctx.beginPath();
          ctx.moveTo(x + rad, y);
          ctx.arcTo(x + w, y, x + w, y + w, rad);
          ctx.arcTo(x + w, y + w, x, y + w, rad);
          ctx.arcTo(x, y + w, x, y, rad);
          ctx.arcTo(x, y, x + w, y, rad);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        } else {
          // miroir : clair si basculable, gris-ardoise épais si soudé
          const fixed = v >= 2;
          ctx.strokeStyle = fixed ? '#5c6a8a' : '#aebfe0';
          ctx.lineWidth = fixed ? Math.max(3, cs * 0.14) : Math.max(2, cs * 0.09);
          ctx.beginPath();
          if ((v & 1) === 0) {
            ctx.moveTo(x0 + cs * 0.18, y0 + cs * 0.82);
            ctx.lineTo(x0 + cs * 0.82, y0 + cs * 0.18);
          } else {
            ctx.moveTo(x0 + cs * 0.18, y0 + cs * 0.18);
            ctx.lineTo(x0 + cs * 0.82, y0 + cs * 0.82);
          }
          ctx.stroke();
        }
      }
    for (const b of p.bombs) {
      ctx.fillStyle = '#ff5470';
      ctx.beginPath();
      ctx.arc((b[1] + 0.5) * cs, (b[0] + 0.5) * cs, cs * 0.16, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#0b1020';
      ctx.font = `${cs * 0.2}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('✕', (b[1] + 0.5) * cs, (b[0] + 0.52) * cs);
    }
    const res = miOkNow(p);
    const beamCols = ['#ff9e00', '#4cc9f0'];
    const allCells = new Set<number>();
    p.ents.forEach((e, i) => {
      const t = miTrace(p.cur, p.N, e);
      t.cells.forEach((c) => allCells.add(c));
      const hit = t.exit && t.exit.side === p.targets[i].side && t.exit.idx === p.targets[i].idx;
      ctx.strokeStyle = res.ok ? '#22e0a3' : hit ? '#9ef0c8' : beamCols[i % 2];
      ctx.lineWidth = Math.max(2.5, cs * 0.07);
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.shadowColor = ctx.strokeStyle;
      ctx.shadowBlur = cs * 0.3;
      ctx.beginPath();
      t.pts.forEach((pt, k) => {
        if (k) ctx.lineTo(pt[0] * cs, pt[1] * cs);
        else ctx.moveTo(pt[0] * cs, pt[1] * cs);
      });
      ctx.stroke();
      ctx.shadowBlur = 0;
      const ep = t.pts[0];
      ctx.fillStyle = '#ffd166';
      ctx.beginPath();
      ctx.arc(ep[0] * cs, ep[1] * cs, cs * 0.14, 0, Math.PI * 2);
      ctx.fill();
    });
    for (const g of p.gems) {
      const on = allCells.has(g[0] * p.N + g[1]);
      const gx = (g[1] + 0.5) * cs,
        gy = (g[0] + 0.5) * cs,
        rr = cs * 0.17;
      ctx.fillStyle = on ? '#22e0a3' : '#b388ff';
      ctx.shadowColor = ctx.fillStyle;
      ctx.shadowBlur = on ? cs * 0.4 : cs * 0.15;
      ctx.beginPath();
      ctx.moveTo(gx, gy - rr);
      ctx.lineTo(gx + rr, gy);
      ctx.lineTo(gx, gy + rr);
      ctx.lineTo(gx - rr, gy);
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
    }
    p.targets.forEach((t, i) => {
      let gx: number, gy: number;
      if (t.side === 'E') {
        gx = S - cs * 0.1;
        gy = (t.idx + 0.5) * cs;
      } else if (t.side === 'W') {
        gx = cs * 0.1;
        gy = (t.idx + 0.5) * cs;
      } else if (t.side === 'S') {
        gx = (t.idx + 0.5) * cs;
        gy = S - cs * 0.1;
      } else {
        gx = (t.idx + 0.5) * cs;
        gy = cs * 0.1;
      }
      ctx.strokeStyle = res.ok ? '#22e0a3' : beamCols[i % 2];
      ctx.lineWidth = cs * 0.06;
      ctx.shadowColor = ctx.strokeStyle;
      ctx.shadowBlur = cs * 0.35;
      ctx.beginPath();
      ctx.arc(gx, gy, cs * 0.17, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    });
  },
  solveIt(p) {
    // fixes/vides/murs sont identiques dans sol et cur par construction
    p.cur = deep(p.sol);
  },
};
